import React, { useEffect, useMemo } from "react";
import { Box, Skeleton } from "@mui/material";
import { IOptionGeneric } from "../../../interface";
import { useUrgentVisitStore } from "../../../../hooks/redux-hooks/urgent-visit-store/urgent-visit.store";
import { TimeSlotGrid } from "./TimeSlotGrid";
import { PetAvatars } from "../../../../components";
import SelectableMonth from "../../../../components/SelectableMonthCalendar/SelectableMonth";
import CheveronDownSmallIcon from "../../../../components/Icons/CheveronDownSmallIcon";
import { CalendarIcon } from "../../../../components/Icons";
import { useUrgentVisitAvailableTimes } from "../../../../hooks/urgent-visit/useUrgentVisitAvailableTimes";
import Loading from "../../../../components/Loading/Loading";
import { InfoIcon } from "../../../../components/Icons";
import { getFirstDayOfMonth, getUnixTimestamp } from "../../../../utils";
import { fromUnixToDateString, getHourFromUnix } from "../../../../utils/unix-timestamp";
import { useAppointmentStore } from "../../../../hooks/redux-hooks/appointment-store/appointment.store";

function getFormattedDateFromEpoch(epoch: number): string {
  const date = new Date(fromUnixToDateString(epoch, "GMT"));
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

function getFormattedDayFromEpoch(epoch: number): string {
  const date = new Date(fromUnixToDateString(epoch, "GMT"));
  return date.toLocaleDateString("en-US", { weekday: "long" });
}

export function injectScheduledSlot(
  slots: IOptionGeneric[],
  appointmentStartAt: number | undefined,
  selectedDateEpoch: number,
  timeZone: string,
): IOptionGeneric[] {
  if (!appointmentStartAt) return slots;

  const slotDateStr = new Date(selectedDateEpoch * 1000).toLocaleDateString("en-US", { timeZone: "UTC" });
  const apptDateStr = new Date(appointmentStartAt * 1000).toLocaleDateString("en-US", { timeZone });
  if (slotDateStr !== apptDateStr) return slots;

  const apptTimeValue = String(appointmentStartAt);
  if (slots.some((s) => s.value === apptTimeValue)) {
    return slots.map((s) =>
      s.value === apptTimeValue ? { ...s, disabled: true } : s,
    );
  }

  const disabledSlot: IOptionGeneric = {
    label: getHourFromUnix(appointmentStartAt, timeZone),
    value: apptTimeValue,
    disabled: true,
  };

  const merged = [...slots, disabledSlot];
  merged.sort((a, b) => Number(a.value) - Number(b.value));
  return merged;
}

export const HomeHospitalSlots = () => {
  const todayEpoch = getUnixTimestamp(new Date());
  const {
    selectedPets, selectedSlot, selectedLocationId,
    setSelectedSlot, setSelectedLocation, setSelectedDate,
    rescheduleAppointmentId,
  } = useUrgentVisitStore();
  const { appointmentDetails } = useAppointmentStore();
  const {
    timeSlots, loading, locationDetails, loadHomeHospitalSlots,
    selectedDateEpoch, fullMonthDaysList,
  } = useUrgentVisitAvailableTimes();

  // Allow selecting any future date so users can see
  // nearby hospital availability even when the home
  // hospital is closed on that day (e.g., Sundays).
  const enabledMonthDaysList = useMemo(
    () => fullMonthDaysList.map((day) => ({ ...day, disabled: false })),
    [fullMonthDaysList],
  );

  const isRescheduleAtThisLocation = rescheduleAppointmentId &&
    appointmentDetails?.location?.idLocation === locationDetails?.idLocation;

  const displaySlots = isRescheduleAtThisLocation ?
    injectScheduledSlot(
      timeSlots,
      appointmentDetails?.start_at,
      selectedDateEpoch,
      locationDetails?.timeZone || "America/Chicago",
    ) : timeSlots;

  const currentPet = selectedPets[0];
  const petName = selectedPets.length === 1 ?
    currentPet?.name :
    selectedPets.map((p) => p.name).join(" and ");

  useEffect(() => {
    loadHomeHospitalSlots();
  }, [loadHomeHospitalSlots]);

  const isToday = selectedDateEpoch === todayEpoch;

  const handleDateSelected = (date: Date) => {
    const epoch = getUnixTimestamp(date);
    setSelectedDate(epoch);
  };

  const handleMonthChange = (newMonth: Date) => {
    const epoch = getUnixTimestamp(getFirstDayOfMonth(newMonth));
    setSelectedDate(epoch);
  };

  const handleSlotSelect = (slot: IOptionGeneric) => {
    setSelectedSlot({
      time: slot.value,
      resourcesId: slot.resourcesId,
      label: slot.label,
    });

    if (locationDetails) {
      setSelectedLocation(
        locationDetails.idLocation,
        locationDetails,
      );
    }
  };

  const dateElement = (
    <div className="flex gap-2 items-center">
      <CalendarIcon color="#262626" />
      {isToday ? (
        <>
          <span className="font-semibold">{"Today,"} </span>
          <span>{getFormattedDateFromEpoch(selectedDateEpoch)}</span>
        </>
      ) : selectedDateEpoch && !loading ? (
        <>
          <span className="font-semibold">{getFormattedDayFromEpoch(selectedDateEpoch)}, </span>
          <span>{getFormattedDateFromEpoch(selectedDateEpoch)}</span>
        </>
      ) : (
        <Box sx={{ width: 80, height: 20, ml: "0.2rem" }}>
          <Skeleton animation="wave" />
        </Box>
      )}
    </div>
  );

  return (
    <div className="w-full flex flex-col gap-4 border border-taupe-50 rounded-xl p-4">
      <div className="flex justify-between items-center gap-2">
        <p className="grenette-pro font-semibold text-lg leading-5 text-light-black">
          {`Select a time for ${petName || ""}'s Urgent Visit.`}
        </p>
        <PetAvatars
          height={48}
          width={48}
          petDetails={selectedPets}
        />
      </div>

      <SelectableMonth
        onSelected={handleDateSelected}
        icon={CheveronDownSmallIcon}
        fullMonthDaysList={enabledMonthDaysList}
        currentDay={selectedDateEpoch}
        handleMonthChange={handleMonthChange}
        loading={loading}
        dateElement={dateElement}
      />

      {loading ? (
        <div className="flex w-full justify-center items-center py-8">
          <Loading />
        </div>
      ) : displaySlots.length ? (
        <TimeSlotGrid
          gridId="home"
          slots={displaySlots}
          selectedValue={
            selectedLocationId === locationDetails?.idLocation ?
              selectedSlot?.time || null : null
          }
          onSelect={handleSlotSelect}
        />
      ) : (
        <div className="flex gap-2 border border-gv-yellow bg-gv-yellow-light p-3 rounded-lg items-center">
          <InfoIcon width={20} height={20} />
          <span className="text-sm text-light-black">
            {`There is no availability for ${isToday ? "Today" : getFormattedDateFromEpoch(selectedDateEpoch)}.`}
          </span>
        </div>
      )}
    </div>
  );
};
